<!--
Fork addition (Stage F): game-data version stamp in the app bar.
Reads public/version.json at runtime (shipped as dist/version.json)
so bumping versions later is a one-file edit — never hard-code
version strings in components. Shows the packaged dataset's game
version next to the latest known game version; when they differ the
stamp flags the data as possibly stale in amber. Informational only:
if version.json cannot be loaded the stamp renders nothing.
-->
<script setup lang="ts">
import {computed, onMounted, ref} from 'vue';
import {mdiAlert} from '@mdi/js';

type VersionInfo = {
    dataGameVersion: string;
    latestGameVersion: string;
    latestChecked: string;
    plannerVersion: string;
};

const versionInfo = ref<VersionInfo | undefined>();

onMounted(async () => {
    try {
        const response = await fetch('version.json');
        if(!response.ok) {
            return;
        }
        const data = await response.json() as Partial<VersionInfo>;
        if((typeof (data.dataGameVersion) === 'string') && (typeof (data.latestGameVersion) === 'string')
            && (typeof (data.latestChecked) === 'string') && (typeof (data.plannerVersion) === 'string')) {
            versionInfo.value = data as VersionInfo;
        }
    } catch {
        //a missing/unparsable version.json must never break the app
    }
});

const isStale = computed(() => {
    const info = versionInfo.value;
    return !!info && (info.dataGameVersion !== info.latestGameVersion);
});

const stampTitle = computed(() => {
    const info = versionInfo.value;
    if(!info) {
        return '';
    }
    const base = `Planner build: ${info.plannerVersion}. Packaged dataset is pinned to game v${info.dataGameVersion}; latest game version v${info.latestGameVersion} (checked ${info.latestChecked}).`;
    return isStale.value
        ? `${base} The game has been updated since the dataset was packaged — recipes and ratios may differ from the current game.`
        : `${base} Dataset matches the latest game version.`;
});
</script>

<template>
    <div v-if="versionInfo" class="version-stamp text-caption" :title="stampTitle">
        <span class="version-stamp-text">Data: game v{{ versionInfo.dataGameVersion }} · Latest: v{{ versionInfo.latestGameVersion }}</span>
        <span v-if="isStale" class="version-stale">
            <v-icon :icon="mdiAlert" size="small" />
            <span>data may be stale</span>
        </span>
        <!-- fork addition (Stage I): compact phone form — the stale
             warning stays visible, the rest collapses to the data
             version (the full text remains in the title tooltip) -->
        <span class="version-stamp-compact" :class="{'version-compact-stale': isStale}">
            <v-icon v-if="isStale" :icon="mdiAlert" size="small" />
            <span>data v{{ versionInfo.dataGameVersion }}</span>
        </span>
    </div>
</template>

<style scoped>
.version-stamp {
    display: flex;
    align-items: center;
    gap: 6px;
    /* may give up space to the title/toolbar on narrow widths */
    flex: 0 1 auto;
    min-width: 0;
    margin-left: 8px;
    white-space: nowrap;
    opacity: 0.75;
}
.version-stamp-text {
    overflow: hidden;
    text-overflow: ellipsis;
}
.version-stale {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    flex: 0 0 auto;
    font-weight: 700;
    color: #e65100;
    opacity: 1;
}
.v-theme--dark .version-stale {
    color: #ffb74d;
}
/* fork addition (Stage I): compact phone form (see template) */
.version-stamp-compact {
    display: none;
}
@media (max-width: 900px) {
    .version-stamp-text, .version-stale {
        display: none;
    }
    .version-stamp-compact {
        display: inline-flex;
        align-items: center;
        gap: 2px;
        white-space: nowrap;
    }
    .version-compact-stale {
        color: #e65100;
        font-weight: 700;
        opacity: 1;
    }
    .v-theme--dark .version-compact-stale {
        color: #ffb74d;
    }
}
</style>
